import { DndContext, type DragEndEvent, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowCardTokenList } from './flow-card-token';
import { FlowEventCard } from './flow-event-card';
import { PAUSE_MESSAGE } from './flow-paused-notice';
import { FlowReorderColumn } from './flow-reorder-column';

import type { FlowCardRef } from '@/lib/flow-utils';
import type { Block, Task } from '@/types/experience';
import type { FlowAction, SensorRule, SensorRung } from '@agavi/blocks';

import { Button } from '@/components/ui/button';
import { getBlockLabel, getOutcomeProblem, sensorCardRefsForBlock } from '@/lib/flow-utils';

/**
 * What happens when the hardware fails — ONE card, with per-sensor rows on request.
 *
 * A fixed rung rather than an event a teacher writes, for one reason: unauthored, a dead GPS falls
 * to Default with no route and no explanation, and the teacher is never asked the question.
 *
 * Consolidated by default. The common field case is "any sensor is unreliable here, send them to
 * the retry task", and a row per block asked that same question once per block — which is also why
 * this reads in the ladder like `Any Incomplete` does now: a short name, and a token list naming
 * the cards it catches. Split reopens the rows for the rarer case where a dead GPS and a dead
 * compass want different destinations, and ordering only means anything there, because two sensors
 * can fail on one submission.
 *
 * Deliberately NOT a "signal help" behaviour with its own special handling. The block reports a
 * sensor problem; the flow answers with the same primitives as everywhere else (stay, next, go to a
 * particular task).
 */

interface FlowSensorRungProps {
  rung: SensorRung;
  /** The rung's own name, and the cards it catches — both supplied by the caller, because the two
   *  rungs differ in nothing else. */
  title: string;
  cards: FlowCardRef[];
  /** Which fault this rung is about, so a split row can name the cards for its own block. */
  kind: 'noData' | 'poorData';
  /** The blocks that can report THIS kind of fault. Empty hides the rung entirely. */
  sensing: Block[];
  /** See FlowEventCard: nothing follows this task, so "go" has to ask where. */
  noNextTask?: boolean;
  blocks: Block[];
  tasks: Task[];
  currentTaskId: string;
  onCreateDetourTask?: (names: { pathwayName: string; taskTitle: string }) => Promise<string | null>;
  onChange: (rung: SensorRung, label: string) => void;
}

export function FlowSensorRung({ rung, title, cards, kind, sensing, blocks, tasks, currentTaskId, onCreateDetourTask, noNextTask, onChange }: FlowSensorRungProps) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
  );
  /**
   * A rung routes like anything else, so it can route somewhere deleted like anything else.
   *
   * Outcome only: a rung has no conditions of its own -- the block reporting a fault IS the
   * condition -- so "incomplete" and "drifted" cannot apply to it. Nothing about sensors is
   * special here; this is the same dead target any action can hold, and the rungs were simply
   * one of two surfaces the pause had not reached.
   */
  const actionProblem = (action: FlowAction) => getOutcomeProblem({ action }, blocks, tasks);
  const actionPause = (action: FlowAction) => {
    const problem = actionProblem(action);
    if (!problem) return undefined;
    return t(PAUSE_MESSAGE[problem.kind], {
      title: (action.type === 'goto' ? action.targetTitle : undefined) ?? t('task.flow.rule.pausedTargetUnnamed'),
    });
  };

  const [lastMoved, setLastMoved] = useState<{ id: string; direction: 'up' | 'down' } | null>(null);
  const upRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const downRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (!lastMoved) return;
    // The button just pressed can land disabled at its new boundary position, and a disabled
    // element cannot take focus — fall back to the opposite button on the same row.
    const primary = lastMoved.direction === 'up' ? upRefs.current.get(lastMoved.id) : downRefs.current.get(lastMoved.id);
    const fallback = lastMoved.direction === 'up' ? downRefs.current.get(lastMoved.id) : upRefs.current.get(lastMoved.id);
    (primary && !primary.disabled ? primary : fallback)?.focus();
    setLastMoved(null);
  }, [rung.rules, lastMoved]);

  // A row whose block is gone is dropped on load (see normalizeFlowConfig), so anything left here
  // resolves — but render defensively rather than crash on a config this component was handed
  // before that ran.
  const rows = (rung.rules ?? []).filter((r) => sensing.some((b) => b.id === r.blockId));
  // No block can report this fault, so there is no question to ask. This is what keeps the No
  // Sensor Data rung off screen until the blocks grow a state for it.
  if (sensing.length === 0) return null;

  function setRung(next: Partial<SensorRung>, label: string) {
    onChange({ action: rung.action, split: rung.split, rules: rung.rules ?? [], ...next }, label);
  }

  function move(id: string, direction: 'up' | 'down') {
    const from = rows.findIndex((r) => r.blockId === id);
    const to = direction === 'up' ? from - 1 : from + 1;
    if (from === -1 || to < 0 || to >= rows.length) return;
    setRung({ rules: arrayMove(rows, from, to) }, t('task.flow.undo.moveSensor'));
    setLastMoved({ id, direction });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((r) => r.blockId === String(active.id));
    const to = rows.findIndex((r) => r.blockId === String(over.id));
    if (from !== -1 && to !== -1) setRung({ rules: arrayMove(rows, from, to) }, t('task.flow.undo.moveSensor'));
  }

  /*
   * Split/Collapse lives INSIDE the card, on the hint line, opposite what it describes.
   *
   * It has now been three places. A heading above the card made these the only navigation entries
   * with a titlehead, competing with `Changes` and `Navigation`. A bare button below the card
   * floated between two cards and belonged to neither. On the hint line it sits in the card it
   * reshapes, beside the sentence saying what that card catches — which is the thing being split.
   *
   * Only offered with more than one sensor block: splitting one produces the same single
   * destination under a second layer of UI.
   */
  const splitToggle = sensing.length > 1 ? (
    <Button
      variant='subtle'
      size='sm'
      className='-my-1 shrink-0'
      onClick={() => setRung({ split: !rung.split }, t('task.flow.undo.setSensor'))}
    >
      {rung.split ? t('task.flow.sensor.collapse') : t('task.flow.sensor.split')}
    </Button>
  ) : null;

  if (!rung.split || rows.length === 0) {
    return (
      <div className='flex flex-col gap-2'>
        <FlowEventCard
          /*
           * Named, even though the heading above says the same words.
           *
           * A card with no title is a headless row — the cycler and the destination with nothing
           * between them saying what they are about. The duplication is real and it is the lesser
           * cost, and it is confined to this one state: split, and the heading stays while each row
           * takes its own block's name.
           */
          title={title}
          hint={(
            <div className='flex items-center justify-between gap-2'>
              <FlowCardTokenList lead={t('task.flow.card.anySensorIs')} cards={cards} connector={t('task.flow.card.connectorOr')} />
              {splitToggle}
            </div>
          )}
          category='sensor'
          action={rung.action}
          problem={actionPause(rung.action)}
          paused={!!actionProblem(rung.action)}
          onActionChange={(action) => setRung({ action }, t('task.flow.undo.setSensor'))}
          tasks={tasks}
          currentTaskId={currentTaskId}
          onCreateDetourTask={onCreateDetourTask}
          noNextTask={noNextTask}
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      {/* Split, the rows are their own cards and none of them owns the control — so it leads the
          group rather than trailing it, where a button under the last row would read as that row's.
          Right-aligned either way, so it stays in the same column as when collapsed. */}
      <div className='flex justify-end'>{splitToggle}</div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        accessibility={{
          screenReaderInstructions: { draggable: t('task.flow.a11y.dragInstructions') },
          announcements: {
            onDragStart: ({ active }) => t('task.flow.a11y.dragStart', { position: rows.findIndex((r) => r.blockId === String(active.id)) + 1, total: rows.length }),
            onDragOver: ({ over }) => (over ? t('task.flow.a11y.dragOver', { position: rows.findIndex((r) => r.blockId === String(over.id)) + 1, total: rows.length }) : undefined),
            onDragEnd: ({ over }) => (over ? t('task.flow.a11y.dragDropped', { position: rows.findIndex((r) => r.blockId === String(over.id)) + 1, total: rows.length }) : undefined),
            onDragCancel: () => t('task.flow.a11y.dragCancelled'),
          },
        }}
      >
        <SortableContext items={rows.map((r) => r.blockId)} strategy={verticalListSortingStrategy}>
          <div className='flex flex-col gap-2'>
            {rows.map((rule, index) => (
              <SensorRow
                key={rule.blockId}
                rule={rule}
                kind={kind}
                index={index}
                total={rows.length}
                blocks={blocks}
                tasks={tasks}
                currentTaskId={currentTaskId}
                onCreateDetourTask={onCreateDetourTask}
                noNextTask={noNextTask}
                onActionChange={(action) => setRung(
                  { rules: rows.map((r) => (r.blockId === rule.blockId ? { ...r, action } : r)) },
                  t('task.flow.undo.setSensor'),
                )}
                onMoveUp={() => move(rule.blockId, 'up')}
                onMoveDown={() => move(rule.blockId, 'down')}
                upRef={(el) => { if (el) upRefs.current.set(rule.blockId, el); else upRefs.current.delete(rule.blockId); }}
                downRef={(el) => { if (el) downRefs.current.set(rule.blockId, el); else downRefs.current.delete(rule.blockId); }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SensorRow({ rule, kind, index, total, blocks, tasks, currentTaskId, onCreateDetourTask, noNextTask, onActionChange, onMoveUp, onMoveDown, upRef, downRef }: {
  rule: SensorRule;
  kind: 'noData' | 'poorData';
  index: number;
  total: number;
  blocks: Block[];
  tasks: Task[];
  currentTaskId: string;
  onCreateDetourTask?: (names: { pathwayName: string; taskTitle: string }) => Promise<string | null>;
  noNextTask?: boolean;
  onActionChange: (action: SensorRule['action']) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  upRef: (el: HTMLButtonElement | null) => void;
  downRef: (el: HTMLButtonElement | null) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.blockId });
  const blockIndex = blocks.findIndex((b) => b.id === rule.blockId);
  const block = blocks[blockIndex];
  // Each split row routes independently, so each is checked independently.
  const rowProblem = getOutcomeProblem({ action: rule.action }, blocks, tasks);
  const rowPause = rowProblem
    ? t(PAUSE_MESSAGE[rowProblem.kind], {
        title: (rule.action.type === 'goto' ? rule.action.targetTitle : undefined)
          ?? t('task.flow.rule.pausedTargetUnnamed'),
      })
    : undefined;
  const name = block ? getBlockLabel(block, blockIndex, t) : t('task.flow.rule.unknownBlock');

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1 ${isDragging ? 'opacity-40' : ''}`}
    >
      <FlowReorderColumn
        index={index}
        total={total}
        dragLabel={t('task.flow.sensor.dragLabel', { block: name })}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        upRef={upRef}
        downRef={downRef}
        listeners={listeners}
        attributes={attributes}
      />
      <div className='min-w-0 flex-1'>
        <FlowEventCard
          // "GPS 1 is [Low Accuracy]", the same shape the whole-task rungs use one level up — a
          // short name, then the cards it catches, from the one derivation both read.
          title={name}
          hint={<FlowCardTokenList
            lead={t('task.flow.card.blockIs', { block: name })}
            cards={block ? sensorCardRefsForBlock(block, kind, t) : []}
            connector={t('task.flow.card.connectorOr')} />}
          category='sensor'
          action={rule.action}
          problem={rowPause}
          paused={!!rowProblem}
          onActionChange={onActionChange}
          tasks={tasks}
          currentTaskId={currentTaskId}
          onCreateDetourTask={onCreateDetourTask}
          noNextTask={noNextTask}
          // A row has no conditions of its own — the block reporting a problem IS the condition —
          // so there is nothing to match, summarize, or expand.
        />
      </div>
    </div>
  );
}
